/* eslint-disable css-modules/no-unused-class */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import startsWith from 'lodash/startsWith';
// jl-design-system
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';
import Tooltip from 'jl-design-system/components/tooltip/Tooltip';
// classnames
import classNames from 'classnames';
// helpers
import { shouldShowCollectionHelpText } from '../../utils/collection/collectionHelp';
import { shouldShowPersonalisationHelpText } from '../../utils/personalisation/personalisationHelp';
import {
  getInformationList,
  getSubtotalText,
  getQuantityText,
  getQuantityTextFabricItem,
  getQuantitySelector,
  getReducedQuantityText,
  getReducedQuantityTextForFabricItem,
} from './OrderItemHelper';
import ProductImageWithMagnifier from '../product-image-with-magnifier/ProductImageWithMagnifier';
// styles
import styles from './order-item.scss';

export class OrderItem extends PureComponent {

  static propTypes = {
    availability: PropTypes.shape({
      preOrder: PropTypes.bool,
    }),
    basketView: PropTypes.bool,
    batchedView: PropTypes.bool,
    child: PropTypes.bool.isRequired,
    displayImage: PropTypes.bool.isRequired,
    hideOutOfStockText: PropTypes.bool,
    hideQuantities: PropTypes.bool,
    hideSubtotals: PropTypes.bool,
    id: PropTypes.string,
    imageUrl: PropTypes.string,
    information: PropTypes.arrayOf(PropTypes.object),
    isPosCreditItem: PropTypes.bool,
    modal: PropTypes.bool,
    name: PropTypes.string.isRequired,
    partnerDiscount: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    partnerDiscountEnabled: PropTypes.bool,
    preventEditing: PropTypes.bool,
    priceHistory: PropTypes.shape({}),
    quantity: PropTypes.number.isRequired,
    reducedQuantity: PropTypes.number,
    removeItem: PropTypes.func,
    showCollectionHelp: PropTypes.bool,
    showPersonalisationMessage: PropTypes.bool,
    sidebar: PropTypes.bool,
    stockLevel: PropTypes.number,
    subtotal: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    subtotalDiscounted: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    type: PropTypes.string,
    uom: PropTypes.string,
  };

  static defaultProps = {
    availability: {},
    basketView: false,
    batchedView: false,
    child: false,
    hideOutOfStockText: false,
    hideQuantities: false,
    hideSubtotals: false,
    id: '',
    imageUrl: '',
    information: undefined,
    isPosCreditItem: false,
    modal: false,
    name: '',
    partnerDiscount: undefined,
    partnerDiscountEnabled: false,
    preventEditing: false,
    priceHistory: undefined,
    reducedQuantity: undefined,
    removeItem: window.defaultFunc,
    showCollectionHelp: false,
    showPersonalisationMessage: false,
    sidebar: false,
    stockLevel: undefined,
    subtotal: '',
    subtotalDiscounted: undefined,
    type: undefined,
    uom: '',
  };

  getContainerClassnames = () => {
    const { basketView, batchedView, child, sidebar, modal, isPosCreditItem } = this.props;

    return classNames(styles.container, {
      [styles.basketView]: basketView,
      [styles.batchedView]: batchedView,
      [styles.containerChild]: child,
      [styles.sidebar]: sidebar,
      [styles.modal]: modal,
      [styles.posCredit]: isPosCreditItem,
    });
  };

  getItemDetailsContainerClassnames = () => {
    const { displayImage, sidebar } = this.props;

    return classNames(styles.itemDetailsContainer, {
      [styles.imageMarginApplied]: !displayImage,
      [styles.sideBarContainer]: sidebar,
    });
  };

  render() {
    const {
      availability,
      batchedView,
      name,
      quantity,
      subtotal,
      subtotalDiscounted,
      imageUrl,
      displayImage,
      uom,
      information,
      partnerDiscount,
      partnerDiscountEnabled,
      sidebar,
      type,
      hideSubtotals,
      hideQuantities,
      reducedQuantity,
      stockLevel,
      hideOutOfStockText,
      id,
      preventEditing,
      priceHistory: {
        reducedToClear,
        originalSubtotal,
      } = {},
      removeItem,
      showCollectionHelp,
      showPersonalisationMessage,
    } = this.props;

    const isItemFabric = uom !== '' && !startsWith(type, 'm2m');

    return (

      <div className={this.getContainerClassnames()} data-test="items">
        {
          displayImage ? (
            <ProductImageWithMagnifier
              imageUrl={imageUrl}
              name={name}
              showMagnifyingIcon={sidebar}
            />
          ) : null
        }

        <div className={this.getItemDetailsContainerClassnames()} data-test="item-details-container">
          <div className={styles.itemType}>
            <p
              className={styles.itemName}
              data-test="order-item-name"
            >
              {name}
            </p>
            {
              showCollectionHelp &&
              <p className={styles.showCollectionHelp}>Collection unavailable</p>
            }
            {
              showPersonalisationMessage &&
              <div className={styles.personalisationTooltip}>
                <Tooltip
                  anchor={sidebar ? 'right' : 'left'}
                  buttonLabel="Includes personalisation"
                  icon
                  id={`personalisationTooltip-${id}`}
                >
                  <p>
                    Once you’ve placed your order, please email your personalisation details to the supplier.
                  </p>
                  <p>
                    Details on how to do this can be found on the product details page or the order confirmation page.
                  </p>
                </Tooltip>
              </div>
            }
            {
              availability.preOrder &&
              <p className={styles.preOrderLabel}>Pre-order</p>
            }
            {
              information && !batchedView &&
              getInformationList(information, styles)
            }
          </div>

          {
            reducedQuantity &&
            <>
              {
                !isItemFabric && (preventEditing || reducedQuantity === 1) &&
                <div className={styles.reducedQuantityContainer}>
                  { getReducedQuantityText(quantity, reducedQuantity, styles) }
                </div>
              }
              {
                isItemFabric && (preventEditing || reducedQuantity === 1) &&
                <div className={styles.reducedQuantityContainer}>
                  { getReducedQuantityTextForFabricItem(quantity, reducedQuantity, styles) }
                </div>
              }
              {
                !preventEditing && reducedQuantity > 1 &&
                getQuantitySelector({
                  id,
                  type,
                  uom,
                  availability: {
                    stockLevel,
                  },
                }, reducedQuantity)
              }
            </>
          }

          {
            !reducedQuantity &&
            !hideQuantities &&
            <>
              {
                isItemFabric &&
                getQuantityTextFabricItem(quantity, styles)
              }
              {
                !isItemFabric &&
                getQuantityText(quantity, styles)
              }
            </>
          }

          {
            !hideSubtotals && !batchedView &&
            getSubtotalText(
              partnerDiscount,
              partnerDiscountEnabled,
              sidebar,
              subtotal,
              subtotalDiscounted,
              styles,
              reducedToClear,
              originalSubtotal,
            )
          }

          {
            (!hideOutOfStockText && stockLevel === 0) &&
            <p className={styles.outOfStock}>
              Sorry, this item is no longer available.
              Please <UnderlinedButton
                onClick={removeItem}
              >remove</UnderlinedButton> this item to continue.
            </p>
          }
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state, ownProps) {
  return {
    showCollectionHelp: shouldShowCollectionHelpText({ isChildItem: ownProps.child, id: ownProps.id, state }),
    showPersonalisationMessage: shouldShowPersonalisationHelpText({
      isChildItem: ownProps.child,
      personalised: ownProps.personalised,
      state,
    }),
  };
}

export default connect((state, ownProps) => mapStateToProps(state, ownProps))(OrderItem);
